import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useMemo } from 'react';
import { getPromiseEntity } from '../../utils/utils';
import { kubernetesApiRef } from '../../kubeApi';
import { useAsync } from 'react-use';
import { toSquadFormState } from '../../utils/mappers';

const CLUSTER_NAME = 'nexus-ops-admin'; // use a known cluster name - you'll need to set this to your actual cluster name

function useProductRef() {
  const catalogApi = useApi(catalogApiRef);
  const getForProductCode = async (reportingCode: string) => {
    if (!reportingCode) {
      return '';
    }

    try {
      const entities = await catalogApi.getEntities({
        filter: {
          kind: 'System',
          'metadata.labels.accessacloud.com/reporting-code': reportingCode,
        },
      });
      return entities.items[0] ? stringifyEntityRef(entities.items[0]) : '';
    } catch (error) {
      return '';
    }
  };
  return {
    getForProductCode,
  };
}

export function useSquadFormState(entity: Entity) {
  const promiseEntity = useMemo(() => getPromiseEntity(entity), [entity]);
  const kubernetesApi = useApi(kubernetesApiRef);
  const { getForProductCode } = useProductRef();

  const { loading, value, error } = useAsync(async () => {
    const [group, version] = promiseEntity.apiVersion.split('/');
    const kind = `${promiseEntity.kind.toLowerCase()}s`; // Kubernetes pluralizes resource names
    const name = promiseEntity.metadata.name;
    const namespace = promiseEntity.metadata.namespace;

    const apiPath = namespace
      ? `/apis/${group}/${version}/namespaces/${namespace}/${kind}/${name}`
      : `/apis/${group}/${version}/${kind}/${name}`;

    const response = await kubernetesApi.proxy({
      clusterName: CLUSTER_NAME,
      path: apiPath,
    });

    if (!response.ok) {
      throw new Error('failed to load squad state');
    }

    const promiseData = await response.json();
    const productCode = promiseData.spec.product_reporting_code ?? '';
    const entityRef = await getForProductCode(productCode);

    return toSquadFormState(promiseData, entityRef);
  });

  return {
    loading,
    value,
    error,
  };
}
